import {
  AppPageHeader,
  ColumnsType,
  InfiniteTable,
} from '@/components/_common';
import { Modal, ModalProps } from 'antd';
import { useIntl } from 'react-intl';
import {
  deliveryMaterialRequest,
  ListStockPartsDeliveryDataSupport,
} from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import { useEffect, useState } from 'react';
import { useStockPartsDelivery } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.hooks';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

interface Props extends ModalProps {
  companyId: number;
  onCancel: () => void;
  partsSelected?: ListStockPartsDeliveryDataSupport[];
  handlerPartsSelect: (part: ListStockPartsDeliveryDataSupport[]) => void;
}

const ModalPartsSelect = ({
  companyId,
  partsSelected,
  handlerPartsSelect,
  ...props
}: Props) => {
  const intl = useIntl().formatMessage;

  const [parts, setParts] = useState<ListStockPartsDeliveryDataSupport[]>([]);

  const [queryParams, setQueryParams] = useState<deliveryMaterialRequest>();

  const { data, isLoading, hasNextPage, fetchNextPage } = useStockPartsDelivery(
    { ...queryParams, companyId },
    props.open,
  );

  const stockPartsDelivery = flatInfiniteResult(data);

  const columns: ColumnsType<ListStockPartsDeliveryDataSupport> = [
    {
      dataIndex: 'workOrderNumber',
      key: 'workOrderNumber',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.os',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      key: 'description',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.description',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value ?? '-';
      },
    },
    useConfigurationColumnsPart({ isSku: true }),
    {
      dataIndex: 'licensePlate',
      key: 'licensePlate',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.board',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value || '-';
      },
    },
    {
      dataIndex: 'requestedQuantity',
      key: 'requestedQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.requestedQuantity',
      }),
      ellipsis: true,
      render: (value) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
    {
      dataIndex: 'availableQuantity',
      key: 'availableQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.availableQuantity',
      }),
      ellipsis: true,
      render: (value) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
    {
      dataIndex: 'provided',
      key: 'provided',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.provided',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value ?? '-';
      },
    },
  ];

  useEffect(() => {
    if (partsSelected) {
      setParts(partsSelected);
    }
  }, [partsSelected]);

  const handleOk = () => {
    if (parts) {
      handlerPartsSelect(parts);
    }

    props.onCancel();
  };

  return (
    <Modal {...props} width="95%" destroyOnClose onOk={handleOk}>
      <>
        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, filter: value }))
          }
        />

        <InfiniteTable<ListStockPartsDeliveryDataSupport>
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          loading={isLoading}
          columns={columns}
          rowKey={(row) => row.workOrderItemId}
          dataSource={stockPartsDelivery}
          rowSelection={{
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            onChange: (_, rows) => setParts(rows),
            selectedRowKeys: parts?.map((part) => part?.workOrderItemId),
            getCheckboxProps: (part) => ({
              disabled: partsSelected?.some(
                ({ workOrderItemId }) =>
                  workOrderItemId === part.workOrderItemId,
              ),
            }),
          }}
        />
      </>
    </Modal>
  );
};

export default ModalPartsSelect;
